import { z } from 'zod'
import type { CaretakerType, CaretakerTypeEnum } from '~/types/caretakers'
import type { MealWishId } from '~/types/meal-wishes'
import type { SignupReference, SignupReferenceId } from '~/types/signup-references'
import type { Dayjs } from 'dayjs'
import type { CreateDonationBoxSteps } from '~/types/donations'
import { getErrorMessage } from '~/utils/zodUtils'
import type { SignupReasonId } from '~/types/signup-reasons'
import type { Gender, Locale, UserType } from '#shared/types/users.ts'

export type FoodiePopupType = 'close' | 'disable' | 'hide' | 'open' | 'sign-up'

export interface AddressStep {
  postalCode: string
  houseNumber: string
}

export interface AddressStepFoodie extends AddressStep {
  reasonOption: ReasonOptionsType
  reasonText: string | null
}

export interface SearchAddressParams extends AddressStep {
  userType: UserType | null
}

export const ReasonOptionsArray = [
  'social', // Ik wil graag nieuwe mensen in de buurt leren kennen
  'health', // Zelf kan ik niet (meer) koken door mijn lichamelijke of mentale gezondheid.
  'age', // Koken is door mijn leeftijd lastiger geworden.
  'money', // Door mijn budget is het koken van een gezonde maaltijd lastig.
  'low_energy', // Koken en/of boodschappen doen kost mij te veel energie.
  'eating_healthier', // Ik eet nu voornamelijk kant-en-klaarmaaltijden dus ik wil gezonder/lekkerder eten.
  'caretaker', // Ik ben mantelzorger en zoek ondersteuning.
  'motivation', // Ik ben alleenstaand en kan niet koken/het ontbreekt me aan motivatie.
  'other' // Anders, namelijk... < tekst veld >
] as const

export const ReasonOptions = z.enum(ReasonOptionsArray, getErrorMessage())

export type ReasonOptionsType = z.infer<typeof ReasonOptions>

export interface InfoCaretakerForm {
  firstName: string
  lastName: string
  email: string
  phoneNumber: string
  institute: string | null
  notes: string | null
  type: CaretakerType
  contactForIntake: boolean
  contactForImpact: boolean
}

export interface MealWishesStep {
  amountOfDays: number
  amountOfPeople: number
  generalMealWishes: MealWishId[]
  specificMealWishes: (MealWishId | string)[]
  miscMealWish: string | null
  helpCookDescription: string
}

export interface ContactStepFoodie {
  firstName: string
  lastName: string
  gender: Gender
  noEmail: boolean
  email?: string
  mailUpdatesAccepted: boolean
  phoneNumber: string
  signupReferenceId: SignupReferenceId
  password: string
  agreeToTermsAndConditions: boolean
}

export type FoodieRegistrationIndex = 'address' | 'meal-wishes' | 'contact'

export interface NewFoodie {
  step: FoodieRegistrationIndex

  addressStep: AddressStepFoodie
  mealWishesStep: MealWishesStep
  contactStep: ContactStepFoodie

  caretakerType: CaretakerOptions
  caretakerOptions?: InfoCaretakerForm

  addressButtonName: string
  addressSearchResult?: AddressResponse
  matchRequestCount?: number

  signUpPopup: FoodiePopupType
  isPraatjePot: boolean
  isVoorproefje: boolean
}

export type CookRegistrationIndex =
  | 'personal-details'
  | 'cooking-abilities'
  | 'cook-consent'

export interface PersonalDetailsStep {
  signupReasonId: SignupReasonId
  firstName: string
  lastName: string
  postalCode: string
  houseNumber: string
  email: string
  mailUpdatesAccepted: boolean
  phoneNumber: string
  gender: Gender
  birthdate: Dayjs
  signupReferenceId: SignupReferenceId
  password: string
}

export interface CookingAbilitiesStep {
  generalMealWishes: MealWishId[]
  specificMealWishes: MealWishId[]
  miscMealWish: string | null
  kitchenPreferences: MealWishId[]
  miscKitchenPreference: string | null
  description: string
}

export interface CookConsentStep {
  VOGConsent: boolean
  agreedToHygieneGuidelinesAt: boolean
  agreeToTermsAndConditions: boolean
}

export interface NewCook {
  step: CookRegistrationIndex
  personalDetailsStep: PersonalDetailsStep
  cookingAbilitiesStep: CookingAbilitiesStep
  cookConsentStep: CookConsentStep
  addressSearchResult?: AddressResponse
  matchRequestCount?: number
  isKookmeer: boolean
}

export interface MatchRequestMealWishesStep {
  amountOfDays: number
  amountOfPeople: number
  helpCookDescription: string
}

export interface NewMatchRequest {
  mealWishesStep: MatchRequestMealWishesStep
}

export interface RegistrationFormProgress {
  isConfirmStep: boolean
  hasSubmitted: boolean
  hasFiredEvents?: boolean
  municipality?: string | null
}

export enum CaretakerOptions {
  Personal = 'personal',
  Caretaker = 'caretaker'
}

export enum PromotionsEnum {
  SantaMaria = 'santa-maria',
  Maggi = 'maggi',
  PraatjePot = 'praatje-pot',
  Voorproefje = 'voorproefje',
  FoodiePopUp = 'foodie-pop-up',
  Kookmeer = 'kookmeer'
}

export interface SearchAddressResponse {
  address: AddressResponse | string[]
  signupReferences?: SignupReference[]
  matchRequestCount?: number
}

export interface AddressResponse {
  street: string
  houseNumber: string
  postalCode: string
  city: string
  neighborhood: string
  district: string
  municipality: string
  borough: string | null
}

export interface RegisterCaretakerParams {
  firstName: string
  lastName: string
  email: string
  phoneNumber: string
  institute: string | null
  notes: string | null
  type: CaretakerTypeEnum
  contactForIntake: boolean
  contactForImpact: boolean
}

export interface RegisterMatchRequestParams {
  amountOfPeople: number
  timesAWeek: number
  helpCookDescription?: string
}

export interface RegisterUserParams extends AddressResponse {
  firstname: string
  lastname: string
  email?: string
  mailUpdatesAccepted: boolean
  phone: string
  gender: Gender
  password: string
  generalMealWishes: MealWishId[]
  specificMealWishes: MealWishId[]
  miscMealWish: string | null
  locale: Locale
  promotions: string[] | null
  signupReferenceId: SignupReferenceId
}

export interface RegisterFoodieParams extends RegisterUserParams {
  reasonOption?: string
  reasonText?: string | null
  caretaker?: RegisterCaretakerParams
  timesAWeek: number
  amountOfPeople: number
  helpCookDescription?: string
}

export interface RegisterCookParams extends RegisterUserParams {
  description: string
  vogConsent: boolean
  agreedToHygieneGuidelinesAt: boolean
  kitchenPreferences: MealWishId[]
  miscKitchenPreference: string | null
  birthdate: Dayjs
  signupReasonId: SignupReasonId
}

export interface NewCollectorStep {
  firstName: string
  lastName: string
  postalCode: string
  houseNumber: string
  email: string
  mailUpdatesAccepted: boolean
  phoneNumber: string
  password: string
}

export interface NewCollector {
  step: CreateDonationBoxSteps
  personalDetailSteps: NewCollectorStep
  addressSearchResult?: AddressResponse
}

export interface RegisterCollectorParams extends AddressResponse {
  firstname: string
  lastname: string
  phone: string
  email: string
  locale: Locale
  password: string
}
